//* 로그인된 본인의 등록 휴대폰으로 6자리 확인 — 비밀번호 변경(현재 비밀번호 모를 때)용
import { FC, useState } from 'react';

import {
  confirmMePasswordPhoneCode,
  isUnsupportedServer,
  sendMePasswordPhoneCode,
} from '@shared/api/auth/mePasswordPhoneVerification.controller';
import { usePhoneCodeVerification } from '@shared/hooks/usePhoneCodeVerification';
import { formatMmSs } from '@shared/utils/timeFormat.util';
import { Button, Input } from 'antd';

/**
 * 흐름은 로그인 2단계 인증(LoginPhoneVerification)과 같지만 API 가 다르다
 * (challenge 없이 토큰으로, 결과는 로그인 토큰이 아니라 비밀번호 변경 증명 proof).
 * 백엔드 요청 스펙: smartbugo-api/docs/me-password-phone-verification-spec.md
 */

const RESEND_COOLDOWN_SEC = 30;

/** 010-1234-5678 → 010-****-5678 (서버가 phoneMasked 를 주기 전 표시용) */
export const maskPhone = (raw?: string | null): string => {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length < 7) return '';
  return `${digits.slice(0, 3)}-****-${digits.slice(-4)}`;
};

interface Props {
  /** 표시용 — 서버가 phoneMasked 를 주면 그것으로 바꾼다 */
  phoneMasked: string;
  onVerified: (proof: string) => void;
  onCancel: () => void;
}

export const MePhoneVerification: FC<Props> = function MePhoneVerification({
  phoneMasked: initialMasked,
  onVerified,
  onCancel,
}: Props) {
  const [unsupported, setUnsupported] = useState(false);

  const {
    phoneMasked,
    verificationId,
    code,
    setCode,
    sending,
    confirming,
    error,
    setError,
    cooldown,
    codeSeconds,
    handleSend,
    handleConfirm,
  } = usePhoneCodeVerification({
    initialPhoneMasked: initialMasked,
    resendCooldownSec: RESEND_COOLDOWN_SEC,
    send: sendMePasswordPhoneCode,
    confirm: confirmMePasswordPhoneCode,
    onVerified: ({ proof }) => onVerified(proof),
    onSendError: (err) => {
      //* 구서버 — 라우트 자체가 없다. 재시도해도 소용없으니 안내만
      if (!isUnsupportedServer(err)) return false;
      setUnsupported(true);
      return true;
    },
  });

  if (unsupported) {
    return (
      <div className="space-y-3 py-2">
        <p className="text-sm text-gray-700">
          이 서버는 아직 휴대폰 인증으로 비밀번호 변경을 지원하지 않습니다.
          <br />
          현재 비밀번호를 모르시면 관리자에게 문의해 주세요.
        </p>
        <button type="button" className="text-sm underline theme-text-main" onClick={onCancel}>
          현재 비밀번호로 확인하기
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 py-2">
      <p className="text-sm text-gray-600">
        등록된 휴대폰 <b>{phoneMasked || '(번호 없음)'}</b> 으로 인증번호를 보냅니다.
      </p>
      <Button
        onClick={handleSend}
        disabled={sending || cooldown > 0 || !phoneMasked}
        style={{ backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' }}
        block
      >
        {sending
          ? '발송 중...'
          : cooldown > 0
          ? `재발송 (${cooldown}초)`
          : verificationId
          ? '인증번호 재발송'
          : '인증번호 받기'}
      </Button>
      {verificationId && (
        <div className="space-y-2">
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="인증번호 6자리"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6));
              setError(null);
            }}
            onPressEnter={handleConfirm}
            suffix={
              codeSeconds > 0 ? (
                <span className="text-xs tabular-nums text-red-500">{formatMmSs(codeSeconds)}</span>
              ) : undefined
            }
          />
          <Button
            onClick={handleConfirm}
            disabled={confirming || code.length !== 6}
            style={{ backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' }}
            block
          >
            {confirming ? '확인 중...' : '인증 확인'}
          </Button>
        </div>
      )}
      {error && <p className="text-sm text-red-500">{error}</p>}
      <button type="button" className="text-xs text-gray-500 underline" onClick={onCancel}>
        현재 비밀번호로 확인하기
      </button>
    </div>
  );
};
